// 分頁模式：每頁固定筆數，頁碼寫在網址上（可分享某一頁）。分組標題以 CSS sticky 固定。
import { paginate } from '@dayone/core/paginate';
import { useMemo } from 'react';
import { type CatalogItem, type ListEntry, type SectionHeader, toEntries } from '../../filters/catalog.ts';
import { t } from '../../i18n/zh-TW.ts';
import type { LayoutKey } from '../../storage/prefs.ts';
import { ChevronLeft, ChevronRight, ICON } from '../../ui/icons/index.ts';
import { GameCard } from './GameCard.tsx';
import { GroupHeader } from './GroupHeader.tsx';
import { PosterCard } from './PosterCard.tsx';

interface Props {
  items: CatalogItem[];
  layout: LayoutKey;
  grouped: boolean;
  page: number;
  pageSize: number;
  currentYear: number;
  /** 使用者時區的今天（近到遠排序時分出本週／下週，並顯示倒數）；null 表示不分 */
  today: number | null;
  countdownToday: number;
  onPage: (page: number) => void;
  onOpen: (id: string) => void;
}

interface Block {
  header: SectionHeader | null;
  games: Extract<ListEntry, { kind: 'game' }>[];
}

/** 把清單項目切成「段落標題＋遊戲」的區塊：桌面版標題放左欄、遊戲放右側 */
function toBlocks(entries: ListEntry[]): Block[] {
  const blocks: Block[] = [];
  for (const e of entries) {
    if (e.kind === 'header') blocks.push({ header: e, games: [] });
    else {
      if (!blocks.length) blocks.push({ header: null, games: [] });
      blocks.at(-1)?.games.push(e);
    }
  }
  return blocks;
}

export function PagedList({
  items,
  layout,
  grouped,
  page,
  pageSize,
  currentYear,
  today,
  countdownToday,
  onPage,
  onOpen,
}: Props) {
  const current = useMemo(() => paginate(items, page, pageSize), [items, page, pageSize]);
  const blocks = useMemo(() => toBlocks(toEntries(current.items, grouped, today)), [current.items, grouped, today]);
  return (
    <>
      <Pagination page={current.page} pageCount={current.pageCount} onPage={onPage} />
      <div className="plist">
        {blocks.map((b) => (
          <section key={b.header?.key ?? 'all'} className={b.header ? 'tl-section' : 'tl-section tl-plain'}>
            {b.header && (
              <div className="prow-header">
                <GroupHeader header={b.header} />
              </div>
            )}
            <div className={layout === 'grid' ? 'tl-items poster-grid' : 'tl-items'}>
              {b.games.map((e) =>
                layout === 'grid' ? (
                  <PosterCard key={e.key} item={e.item} today={countdownToday} onOpen={onOpen} />
                ) : (
                  <GameCard
                    key={e.key}
                    item={e.item}
                    currentYear={currentYear}
                    today={countdownToday}
                    onOpen={onOpen}
                  />
                ),
              )}
            </div>
          </section>
        ))}
      </div>
      <Pagination page={current.page} pageCount={current.pageCount} onPage={onPage} />
    </>
  );
}

function Pagination({ page, pageCount, onPage }: { page: number; pageCount: number; onPage: (p: number) => void }) {
  if (pageCount <= 1) return null;
  return (
    <nav className="pagination" aria-label={t.pagination.label}>
      <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        <ChevronLeft {...ICON} />
        {t.pagination.prev}
      </button>
      <span className="num" aria-live="polite">
        {t.pagination.page(page, pageCount)}
      </span>
      <button type="button" disabled={page >= pageCount} onClick={() => onPage(page + 1)}>
        {t.pagination.next}
        <ChevronRight {...ICON} />
      </button>
    </nav>
  );
}
